import { css, Rule } from 'glamor'
import * as React from 'react'
import * as PropTypes from 'prop-types'
import url from 'url'

import { isKey } from '../../utils'
import { useTheme } from '../../theme'

import DownloadIcon from './download.svg'
import ExternalLinkIcon from './external-link.svg'
import getStyles from './styles'

const variants = ['regular', 'primary', 'secondary', 'positive', 'negative', 'light']

interface ILinkProps {
  children?: React.ReactNode
  variant?: 'regular' | 'primary' | 'secondary' | 'positive' | 'negative' | 'light'
  block?: boolean
  underline?: boolean
  style?: Rule
  href?: string
  icon?: null | 'external' | 'download'
  rel?: string
  target?: string
  onClick?: (e: React.SyntheticEvent) => void
}

export function Link({ children, variant, style, block, onClick, underline, href, icon, ...additional }: ILinkProps) {
  const styles = getStyles(useTheme())
  const isLink = !!href || !onClick
  const Component = isLink ? 'a' : 'span'
  const currentHostname = url.parse(window.location.href).hostname
  const hrefParts = href ? url.parse(href) : { hostname: undefined, protocol: undefined }
  const isExternal = hrefParts.protocol && currentHostname !== hrefParts.hostname
  const externalProps = isExternal
    ? {
        target: '_blank',
        rel: 'noopener noreferrer',
      }
    : {}
  const keyboardProps = onClick
    ? {
        onKeyPress: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClick(e)
          }
        },
      }
    : {}

  return (
    <Component
      href={href}
      onClick={onClick}
      role={isLink ? 'link' : 'button'}
      {...css([
        styles.link.base,
        isKey(styles.link.variants, variant) && styles.link.variants[variant],
        block && styles.link.block,
        onClick && styles.link.hoverable,
        underline && styles.link.underline,
        style,
      ])}
      tabIndex={0}
      {...externalProps}
      {...keyboardProps}
      {...additional}
    >
      {children}
      {icon === 'download' && <DownloadIcon {...css(styles.downloadIcon)} />}
      {(icon === 'external' || (icon === undefined && isExternal)) && (
        <ExternalLinkIcon {...css(styles.externalLinkIcon)} />
      )}
    </Component>
  )
}

Link.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf(variants),
  block: PropTypes.bool,
  flush: PropTypes.bool,
  underline: PropTypes.bool,
  style: PropTypes.object,
  href: PropTypes.string,
  icon: PropTypes.oneOf([null, 'external', 'download']),
  onClick: PropTypes.func,
}

Link.defaultProps = {
  variant: 'regular',
  underline: false,
}

Link.VARIANTS = variants
